import React, { useState, useEffect } from 'react';
import { Stage, Layer, Rect, Text, Group, Line, Transformer, Image as KonvaImage } from 'react-konva';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  ArrowLeft, 
  Save, 
  RotateCcw, 
  Maximize, 
  Layout, 
  Columns, 
  FileText, 
  AlertCircle,
  Loader2,
  Type,
  Square,
  Minus,
  Trash2,
  Plus,
  MousePointer2,
  Copy,
  Layers,
  Image as ImageIcon,
  PenTool,
  Grid3X3,
  Settings2,
  GripHorizontal
} from 'lucide-react';
import { toast } from 'sonner';
import { CustomElement, LayoutSettings, TemplateSection } from '../types';

const DEFAULT_SETTINGS: LayoutSettings = {
  fontSize: 9.5,
  fontFamily: 'helvetica',
  fontStyle: 'normal',
  marginTop: 12,
  marginBottom: 7,
  marginLeft: 15,
  marginRight: 15,
  infoBoxesY: 45,
  descriptionStartY: 80,
  itemsStartY: 91,
  footerY: 238,
  footerHeight: 52,
  signatureHeight: 18,
  itemsRowHeight: 4.4,
  logoWidth: 35,
  columnWidths: {
    sn: 15,
    qty: 15,
    unit: 15,
    rate: 25,
    amount: 25
  },
  customElements: [],
  sections: []
};

const PAGE_WIDTH_MM = 210;
const PAGE_HEIGHT_MM = 297;
const SCALE = 2.8; // Reduced scale to fit screen better

const URLImage: React.FC<{ url: string; x: number; y: number; width: number; height: number; id?: string; onTransformEnd?: (e: any) => void }> = ({ url, x, y, width, height, id, onTransformEnd }) => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    const img = new window.Image();
    img.src = url;
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      setImage(img);
    };
  }, [url]);

  return <KonvaImage id={id} image={image || undefined} x={x} y={y} width={width} height={height} onTransformEnd={onTransformEnd} />;
};

const InvoiceDesigner: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<LayoutSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [letterheadUrl, setLetterheadUrl] = useState<string | null>(null);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const transformerRef = React.useRef<any>(null);
  const stageRef = React.useRef<any>(null);

  useEffect(() => {
    const brandingUnsub = onSnapshot(doc(db, 'settings', 'branding'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setLogoUrl(data.logoUrl || null);
        setLetterheadUrl(data.letterheadUrl || null);
        setSignatureUrl(data.signatureUrl || null);
      }
    });
    
    const unsub = onSnapshot(doc(db, 'settings', 'invoiceLayout'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setSettings({ 
          ...DEFAULT_SETTINGS, 
          ...data,
          columnWidths: data.columnWidths || DEFAULT_SETTINGS.columnWidths 
        });
      }
      setIsLoading(false);
    });
    return () => {
      unsub();
      brandingUnsub();
    };
  }, []);

  const mmToPx = (mm: number) => mm * SCALE;

  const handleDragEnd = (e: any, key: string) => {
    const pX = e.target.x() / SCALE;
    const pY = e.target.y() / SCALE;
    
    if (key.startsWith('section-')) {
      const id = key;
      setSettings(prev => ({
        ...prev,
        sections: prev.sections?.map(s => s.id === id ? { ...s, x: pX, y: pY } : s)
      }));
      return;
    }
    if (key.startsWith('custom-')) {
      const id = key.replace('custom-', '');
      setSettings(prev => ({
        ...prev,
        customElements: prev.customElements?.map(el => 
          el.id === id ? { ...el, x: pX, y: pY } : el
        )
      }));
      return;
    }

    if (key === 'infoBoxesY' || key === 'descriptionStartY' || key === 'itemsStartY' || key === 'footerY') {
      setSettings(prev => ({ ...prev, [key]: Math.round(pY) }));
      return;
    }

    if (key === 'marginTop') {
      setSettings(prev => ({ ...prev, marginTop: Math.round(pY) }));
      return;
    }

    if (key === 'marginLeft') {
      setSettings(prev => ({ ...prev, marginLeft: Math.round(pX) }));
      return;
    }

    if (key === 'marginRight') {
      setSettings(prev => ({ ...prev, marginRight: Math.round(PAGE_WIDTH_MM - pX) }));
      return;
    }

    if (key === 'marginBottom') {
      setSettings(prev => ({ ...prev, marginBottom: Math.round(PAGE_HEIGHT_MM - pY) }));
      return;
    }
  };

  const addSection = () => {
    const newSection: TemplateSection = {
      id: `section-${Math.random().toString(36).substr(2, 9)}`,
      name: `Section ${ (settings.sections?.length || 0) + 1}`,
      type: 'grid',
      x: 50,
      y: 150,
      width: 110,
      height: 40,
      rows: 2,
      cols: 2,
      style: { border: true }
    };
    setSettings(prev => ({
      ...prev,
      sections: [...(prev.sections || []), newSection]
    }));
    setSelectedId(newSection.id);
  };

  const updateSection = (id: string, updates: Partial<TemplateSection>) => {
    setSettings(prev => ({
      ...prev,
      sections: prev.sections?.map(s => s.id === id ? { ...s, ...updates } : s)
    }));
  };

  const deleteSection = (id: string) => {
    setSettings(prev => ({
      ...prev,
      sections: prev.sections?.filter(s => s.id !== id)
    }));
    setSelectedId(null);
  };

  const duplicateElement = (id: string) => {
    if (id.startsWith('section-')) {
       const sec = settings.sections?.find(s => s.id === id);
       if (!sec) return;
       const newSec = { ...sec, id: `section-${Math.random().toString(36).substr(2, 9)}`, x: sec.x + 5, y: sec.y + 5 };
       setSettings(prev => ({ ...prev, sections: [...(prev.sections || []), newSec] }));
       setSelectedId(newSec.id);
       return;
    }
    const el = settings.customElements?.find(e => e.id === id);
    if (!el) return;
    
    const newEl: CustomElement = {
      ...el,
      id: Math.random().toString(36).substr(2, 9),
      x: el.x + 5,
      y: el.y + 5
    };
    
    setSettings(prev => ({
      ...prev,
      customElements: [...(prev.customElements || []), newEl]
    }));
    setSelectedId(newEl.id);
  };

  const addElement = (type: CustomElement['type'], isSignature?: boolean) => {
    const id = isSignature ? `sign-${Math.random().toString(36).substr(2, 9)}` : Math.random().toString(36).substr(2, 9);
    const newEl: CustomElement = {
      id,
      type,
      x: 50,
      y: 50,
      width: (type === 'rect' || type === 'image') ? 50 : undefined,
      height: (type === 'rect' || type === 'image') ? 20 : undefined,
      text: type === 'text' ? 'New text element' : undefined,
      fontSize: type === 'text' ? 10 : undefined,
      fill: (type === 'line' || type === 'image') ? undefined : '#000000',
      stroke: type === 'line' ? '#000000' : undefined,
      points: type === 'line' ? [0, 0, 50, 0] : undefined,
      opacity: 1
    };
    setSettings(prev => ({
      ...prev,
      customElements: [...(prev.customElements || []), newEl]
    }));
    setSelectedId(id);
  };

  const deleteElement = (id: string) => {
    setSettings(prev => ({
      ...prev,
      customElements: prev.customElements?.filter(el => el.id !== id)
    }));
    setSelectedId(null);
  };

  const updateElement = (id: string, updates: Partial<CustomElement>) => {
    setSettings(prev => ({
      ...prev,
      customElements: prev.customElements?.map(el => 
        el.id === id ? { ...el, ...updates } : el
      )
    }));
  };

  const handleTransformEnd = (e: any) => {
    const node = e.target;
    if (node.id().startsWith('section-')) {
      const id = node.id();
      setSettings(prev => ({
        ...prev,
        sections: prev.sections?.map(s => s.id === id ? {
          ...s,
          x: node.x() / SCALE,
          y: node.y() / SCALE,
          width: (node.width() * scaleX) / SCALE,
          height: (node.height() * scaleY) / SCALE
        } : s)
      }));
      // Reset scale to 1
      node.scaleX(1);
      node.scaleY(1);
      return;
    }

    const id = node.id().replace('custom-', '');
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    // Reset scale to 1 to avoid double-scaling in subsequent transforms
    node.scaleX(1);
    node.scaleY(1);

    setSettings(prev => ({
      ...prev,
      customElements: prev.customElements?.map(el => 
        el.id === id ? { 
          ...el, 
          x: node.x() / SCALE,
          y: node.y() / SCALE,
          width: (node.width() * scaleX) / SCALE,
          height: (node.height() * scaleY) / SCALE
        } : el
      )
    }));
  };

  useEffect(() => {
    if (selectedId && transformerRef.current) {
      const stage = stageRef.current;
      const node = stage.findOne(`#${selectedId}`) || stage.findOne(`#custom-${selectedId}`);
      if (node) {
        transformerRef.current.nodes([node]);
        transformerRef.current.getLayer().batchDraw();
      } else {
        transformerRef.current.nodes([]);
      }
    } else if (transformerRef.current) {
      transformerRef.current.nodes([]);
    }
  }, [selectedId, settings.customElements]);

  const handleColumnResize = (colKey: string, newX: number) => {
    const totalW = PAGE_WIDTH_MM - settings.marginLeft - settings.marginRight;
    const cw = settings.columnWidths || DEFAULT_SETTINGS.columnWidths!;
    
    if (colKey === 'sn') {
        const newVal = Math.max(5, (newX / SCALE) - settings.marginLeft);
        setSettings({ ...settings, columnWidths: { ...cw, sn: Math.round(newVal) } });
    } else if (colKey === 'qty') {
        // Position relative to right edge
        const distFromRight = PAGE_WIDTH_MM - settings.marginRight - (newX / SCALE);
        const newVal = distFromRight - cw.unit - cw.rate - cw.amount;
        setSettings({ ...settings, columnWidths: { ...cw, qty: Math.max(5, Math.round(newVal)) } });
    } else if (colKey === 'unit') {
        const distFromRight = PAGE_WIDTH_MM - settings.marginRight - (newX / SCALE);
        const newVal = distFromRight - cw.rate - cw.amount;
        setSettings({ ...settings, columnWidths: { ...cw, unit: Math.max(5, Math.round(newVal)) } });
    } else if (colKey === 'rate') {
        const distFromRight = PAGE_WIDTH_MM - settings.marginRight - (newX / SCALE);
        const newVal = distFromRight - cw.amount;
        setSettings({ ...settings, columnWidths: { ...cw, rate: Math.max(5, Math.round(newVal)) } });
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await setDoc(doc(db, 'settings', 'invoiceLayout'), settings);
      toast.success('Professional layout arrangement saved');
    } catch (e) {
      console.error('Save error:', e);
      toast.error('Failed to save layout');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-red-600" /></div>;

  const cw = settings.columnWidths || DEFAULT_SETTINGS.columnWidths!;
  const tableX = mmToPx(settings.marginLeft);
  const tableW = mmToPx(PAGE_WIDTH_MM - settings.marginLeft - settings.marginRight);

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col font-sans">
      <div className="h-16 bg-white border-b border-slate-200 px-6 flex items-center justify-between shadow-sm z-20">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/invoices')} className="p-2 hover:bg-slate-50 rounded-xl transition-all"><ArrowLeft className="w-5 h-5 text-slate-400" /></button>
          <h1 className="text-sm font-black text-slate-900 uppercase tracking-tighter">Drafting Table</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setSettings(DEFAULT_SETTINGS)} className="px-4 py-2 text-[10px] font-black uppercase text-slate-500 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all flex items-center gap-2">
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
          <button onClick={handleSave} disabled={isSaving} className="px-6 py-2 bg-red-600 text-white text-[10px] font-black uppercase rounded-xl hover:bg-red-700 transition-all shadow-lg shadow-red-900/20 flex items-center gap-2">
            {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Sync Design
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="w-80 bg-white border-r border-slate-200 p-6 space-y-6 overflow-y-auto shadow-sm">
          {/* Toolbox */}
          <div className="space-y-4">
            <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] flex items-center gap-2 leading-none"><Plus className="w-3 h-3" /> Insert Tool</h3>
            <div className="grid grid-cols-4 gap-2">
              <button onClick={() => addElement('text')} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-100 hover:border-red-200 hover:bg-red-50 transition-all group">
                <Type className="w-4 h-4 text-slate-400 group-hover:text-red-500" />
                <span className="text-[8px] font-black uppercase text-slate-500 group-hover:text-red-600">Text</span>
              </button>
              <button onClick={() => addElement('rect')} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-100 hover:border-red-200 hover:bg-red-50 transition-all group">
                <Square className="w-4 h-4 text-slate-400 group-hover:text-red-500" />
                <span className="text-[8px] font-black uppercase text-slate-500 group-hover:text-red-600">Shape</span>
              </button>
              <button onClick={() => addElement('line')} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-100 hover:border-red-200 hover:bg-red-50 transition-all group">
                <Minus className="w-4 h-4 text-slate-400 group-hover:text-red-500" />
                <span className="text-[8px] font-black uppercase text-slate-500 group-hover:text-red-600">Line</span>
              </button>
              <button onClick={() => addElement('image')} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-100 hover:border-red-200 hover:bg-red-50 transition-all group">
                <ImageIcon className="w-4 h-4 text-slate-400 group-hover:text-red-500" />
                <span className="text-[8px] font-black uppercase text-slate-500 group-hover:text-red-600">Image</span>
              </button>
              <button onClick={() => addElement('image', true)} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-blue-100 hover:border-blue-200 hover:bg-blue-50 transition-all group">
                <PenTool className="w-4 h-4 text-blue-400 group-hover:text-blue-500" />
                <span className="text-[8px] font-black uppercase text-blue-500 group-hover:text-blue-600">Sign</span>
              </button>
              <button onClick={addSection} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-orange-100 hover:border-orange-200 hover:bg-orange-50 transition-all group">
                <Grid3X3 className="w-4 h-4 text-orange-400 group-hover:text-orange-500" />
                <span className="text-[8px] font-black uppercase text-orange-500 group-hover:text-orange-600">Section</span>
              </button>
            </div>
          </div>

          {(selectedId?.startsWith('section-') ? settings.sections?.find(s => s.id === selectedId) : settings.customElements?.find(el => el.id === selectedId)) && (
            <div className="space-y-4 pt-6 border-t border-slate-100 animate-in fade-in slide-in-from-bottom-2">
              <div className="flex items-center justify-between">
                <h3 className="text-[10px] font-black uppercase text-red-600 tracking-[0.2em]">Properties</h3>
                <div className="flex items-center gap-1">
                  <button onClick={() => duplicateElement(selectedId!)} title="Duplicate" className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"><Copy className="w-3.5 h-3.5" /></button>
                  <button onClick={() => selectedId!.startsWith('section-') ? deleteSection(selectedId!) : deleteElement(selectedId!)} title="Delete" className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              
              {(() => {
                const sId = selectedId!;
                if (sId.startsWith('section-')) {
                  const sec = settings.sections!.find(s => s.id === sId)!;
                  return (
                    <div className="space-y-3">
                      <div>
                        <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Section Name</label>
                        <input type="text" value={sec.name} onChange={e => updateSection(sec.id, { name: e.target.value })} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Rows</label>
                          <input type="number" min="1" max="10" value={sec.rows} onChange={e => updateSection(sec.id, { rows: Number(e.target.value) })} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold" />
                        </div>
                        <div>
                          <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Columns</label>
                          <input type="number" min="1" max="10" value={sec.cols} onChange={e => updateSection(sec.id, { cols: Number(e.target.value) })} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Width (mm)</label>
                          <input type="number" value={Math.round(sec.width)} onChange={e => updateSection(sec.id, { width: Number(e.target.value) })} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold" />
                        </div>
                        <div>
                          <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Height (mm)</label>
                          <input type="number" value={Math.round(sec.height)} onChange={e => updateSection(sec.id, { height: Number(e.target.value) })} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold" />
                        </div>
                      </div>
                      <div>
                        <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Cell Mappings</label>
                        <div className="text-[8px] font-bold text-slate-500 uppercase bg-slate-50 p-3 rounded-xl border border-slate-100 flex flex-col gap-1">
                          {Array.from({ length: sec.rows }).map((_, r) => (
                            <div key={r} className="flex gap-1">
                              {Array.from({ length: sec.cols }).map((_, c) => {
                                const key = `${r}-${c}`;
                                return (
                                  <select 
                                    key={c}
                                    value={sec.fields?.[key] || ''} 
                                    onChange={e => {
                                      const newFields = { ...(sec.fields || {}), [key]: e.target.value };
                                      updateSection(sec.id, { fields: newFields });
                                    }}
                                    className="flex-1 bg-white border border-slate-200 rounded p-1 text-[7px]"
                                  >
                                    <option value="">Empty</option>
                                    <option value="invoiceNumber">Inv #</option>
                                    <option value="createdAt">Date</option>
                                    <option value="dueDate">Due Date</option>
                                    <option value="clientName">Client</option>
                                    <option value="totalAmount">Total</option>
                                    <option value="notes">Notes</option>
                                    <option value="signatoryName">Sign Name</option>
                                    <option value="signatoryTitle">Sign Title</option>
                                  </select>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }
                const el = settings.customElements!.find(e => e.id === sId)!;
                return (
                  <div className="space-y-3">
                    {el.type === 'text' && (
                      <div>
                        <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Content</label>
                        <textarea value={el.text} onChange={e => updateElement(el.id, { text: e.target.value })} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold border-2 border-transparent focus:border-red-500 transition-all resize-none" rows={3} />
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">X Position</label>
                        <input type="number" value={Math.round(el.x)} onChange={e => updateElement(el.id, { x: Number(e.target.value) })} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold" />
                      </div>
                      <div>
                        <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Y Position</label>
                        <input type="number" value={Math.round(el.y)} onChange={e => updateElement(el.id, { y: Number(e.target.value) })} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold" />
                      </div>
                    </div>

                    {el.type === 'text' && (
                      <div>
                        <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Font Size</label>
                        <input type="number" value={el.fontSize} onChange={e => updateElement(el.id, { fontSize: Number(e.target.value) })} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold" />
                      </div>
                    )}

                    {el.type === 'rect' && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Width</label>
                          <input type="number" value={Math.round(el.width || 0)} onChange={e => updateElement(el.id, { width: Number(e.target.value) })} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold" />
                        </div>
                        <div>
                          <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Height</label>
                          <input type="number" value={Math.round(el.height || 0)} onChange={e => updateElement(el.id, { height: Number(e.target.value) })} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold" />
                        </div>
                      </div>
                    )}

                    {el.type === 'image' && (
                      <div className="space-y-3">
                         <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Width</label>
                            <input type="number" value={Math.round(el.width || 0)} onChange={e => updateElement(el.id, { width: Number(e.target.value) })} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold" />
                          </div>
                          <div>
                            <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Height</label>
                            <input type="number" value={Math.round(el.height || 0)} onChange={e => updateElement(el.id, { height: Number(e.target.value) })} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold" />
                          </div>
                        </div>
                        <div>
                          <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Image Placeholder</label>
                          <div className="p-4 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center gap-2">
                             <ImageIcon className="w-5 h-5 text-slate-300" />
                             <span className="text-[8px] font-bold text-slate-400 uppercase">Custom Icons/Logo</span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Font Family</label>
                      <select value={el.fontStyle || 'normal'} onChange={e => updateElement(el.id, { fontStyle: e.target.value })} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold">
                        <option value="normal">Normal</option>
                        <option value="bold">Bold</option>
                        <option value="italic">Italic</option>
                        <option value="bolditalic">Bold Italic</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">{el.type === 'line' ? 'Stroke' : 'Fill Color'}</label>
                      <div className="flex gap-2">
                        <input type="color" value={el.type === 'line' ? el.stroke : el.fill} onChange={e => updateElement(el.id, el.type === 'line' ? { stroke: e.target.value } : { fill: e.target.value })} className="h-8 w-12 border-0 bg-transparent cursor-pointer" />
                        <input type="text" value={el.type === 'line' ? el.stroke : el.fill} onChange={e => updateElement(el.id, el.type === 'line' ? { stroke: e.target.value } : { fill: e.target.value })} className="flex-1 px-3 py-1 bg-slate-50 rounded-lg text-[10px] font-mono" />
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          <div className="space-y-4 pt-6 border-t border-slate-100">
            <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] flex items-center gap-2 leading-none"><Layers className="w-3 h-3" /> Global Style</h3>
            <div className="space-y-3">
              <div>
                <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Font Family</label>
                <select value={settings.fontFamily} onChange={e => setSettings({...settings, fontFamily: e.target.value})} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold">
                  <option value="helvetica">Helvetica (Default)</option>
                  <option value="times">Times New Roman</option>
                  <option value="courier">Courier</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Base Size</label>
                  <input type="number" step="0.25" value={settings.fontSize} onChange={e => setSettings({...settings, fontSize: Number(e.target.value)})} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold" />
                </div>
                <div>
                  <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Row Height</label>
                  <input type="number" step="0.1" value={settings.itemsRowHeight} onChange={e => setSettings({...settings, itemsRowHeight: Number(e.target.value)})} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold" />
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-6 border-t border-slate-100">
            <div className="grid grid-cols-2 gap-3">
              {['marginTop', 'marginBottom', 'marginLeft', 'marginRight'].map(m => (
                <div key={m}>
                  <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">{m.replace('margin','')}</label>
                  <input type="number" value={settings[m as keyof LayoutSettings] as number} onChange={e => setSettings({...settings, [m]: Number(e.target.value)})} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold border-2 border-transparent focus:border-red-500 transition-all" />
                </div>
              ))}
              <div className="col-span-2">
                <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">Logo Dimension (mm)</label>
                <div className="flex items-center gap-3">
                  <input type="range" min="10" max="100" value={settings.logoWidth || 35} onChange={e => setSettings({...settings, logoWidth: Number(e.target.value)})} className="flex-1 accent-red-600" />
                  <span className="text-xs font-bold text-slate-600 w-8">{settings.logoWidth || 35}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-6 border-t border-slate-100">
            <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] flex items-center gap-2 leading-none"><Columns className="w-3 h-3" /> Table Columns</h3>
            <div className="grid grid-cols-2 gap-3">
              {Object.keys(cw).map(k => (
                <div key={k}>
                  <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">{k}</label>
                  <input type="number" value={cw[k as keyof typeof cw]} onChange={e => setSettings({...settings, columnWidths: {...cw, [k]: Number(e.target.value)}})} className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold border-2 border-transparent focus:border-red-500 transition-all" />
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex gap-3">
             <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
             <p className="text-[9px] font-bold text-red-800 uppercase leading-relaxed">Drag columns in the "Items Table" area of the canvas to visually resize headers.</p>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-slate-100 p-8 flex justify-center items-start scrollbar-thin scrollbar-thumb-slate-300">
          <div className="bg-white shadow-2xl border border-slate-200 origin-top">
            <Stage 
              ref={stageRef}
              width={PAGE_WIDTH_MM * SCALE} 
              height={PAGE_HEIGHT_MM * SCALE}
              onMouseDown={(e) => {
                if (e.target === e.target.getStage()) {
                  setSelectedId(null);
                }
              }}
            >
              <Layer>
                {/* Margin Guidelines & Handles */}
                <Group>
                  {/* Left Margin */}
                  <Line 
                    points={[mmToPx(settings.marginLeft), 0, mmToPx(settings.marginLeft), mmToPx(PAGE_HEIGHT_MM)]} 
                    stroke="#ef4444" strokeWidth={1} opacity={0.3} dash={[4,4]} 
                  />
                  <Rect 
                    x={mmToPx(settings.marginLeft) - 5} y={0} width={10} height={mmToPx(PAGE_HEIGHT_MM)} 
                    draggable dragBoundFunc={p => ({ x: p.x, y: 0 })}
                    onDragMove={e => setSettings(prev => ({ ...prev, marginLeft: Math.round(e.target.x() / SCALE) }))}
                    onMouseEnter={e => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'col-resize'; }}
                    onMouseLeave={e => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'default'; }}
                  />

                  {/* Right Margin */}
                  <Line 
                    points={[mmToPx(PAGE_WIDTH_MM - settings.marginRight), 0, mmToPx(PAGE_WIDTH_MM - settings.marginRight), mmToPx(PAGE_HEIGHT_MM)]} 
                    stroke="#ef4444" strokeWidth={1} opacity={0.3} dash={[4,4]} 
                  />
                  <Rect 
                    x={mmToPx(PAGE_WIDTH_MM - settings.marginRight) - 5} y={0} width={10} height={mmToPx(PAGE_HEIGHT_MM)} 
                    draggable dragBoundFunc={p => ({ x: p.x, y: 0 })}
                    onDragMove={e => setSettings(prev => ({ ...prev, marginRight: Math.round(PAGE_WIDTH_MM - (e.target.x() / SCALE)) }))}
                    onMouseEnter={e => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'col-resize'; }}
                    onMouseLeave={e => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'default'; }}
                  />

                  {/* Top Margin */}
                  <Line 
                    points={[0, mmToPx(settings.marginTop), mmToPx(PAGE_WIDTH_MM), mmToPx(settings.marginTop)]} 
                    stroke="#ef4444" strokeWidth={1} opacity={0.3} dash={[4,4]} 
                  />
                  <Rect 
                    x={0} y={mmToPx(settings.marginTop) - 5} width={mmToPx(PAGE_WIDTH_MM)} height={10} 
                    draggable dragBoundFunc={p => ({ x: 0, y: p.y })}
                    onDragMove={e => setSettings(prev => ({ ...prev, marginTop: Math.round(e.target.y() / SCALE) }))}
                    onMouseEnter={e => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'row-resize'; }}
                    onMouseLeave={e => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'default'; }}
                  />

                  {/* Bottom Margin */}
                  <Line 
                    points={[0, mmToPx(PAGE_HEIGHT_MM - settings.marginBottom), mmToPx(PAGE_WIDTH_MM), mmToPx(PAGE_HEIGHT_MM - settings.marginBottom)]} 
                    stroke="#ef4444" strokeWidth={1} opacity={0.3} dash={[4,4]} 
                  />
                  <Rect 
                    x={0} y={mmToPx(PAGE_HEIGHT_MM - settings.marginBottom) - 5} width={mmToPx(PAGE_WIDTH_MM)} height={10} 
                    draggable dragBoundFunc={p => ({ x: 0, y: p.y })}
                    onDragMove={e => setSettings(prev => ({ ...prev, marginBottom: Math.round(PAGE_HEIGHT_MM - (e.target.y() / SCALE)) }))}
                    onMouseEnter={e => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'row-resize'; }}
                    onMouseLeave={e => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'default'; }}
                  />
                </Group>

                {/* Header Context */}
                <Group x={tableX} y={mmToPx(settings.marginTop + 2)}>
                  <Rect width={mmToPx(settings.logoWidth || 35)} height={mmToPx(settings.logoWidth || 35)} fill="#f1f5f9" stroke="#cbd5e1" dash={[2,2]} />
                  <Text text="LOGO" x={mmToPx(2)} y={mmToPx(settings.logoWidth || 35) / 2 - 2} width={mmToPx(settings.logoWidth || 35)} align="center" fontSize={mmToPx(3)} fontStyle="bold" fill="#94a3b8" />
                  
                  <Rect x={mmToPx(settings.logoWidth || 35) + mmToPx(10)} width={tableW - mmToPx(settings.logoWidth || 35) - mmToPx(10)} height={mmToPx(35)} fill="#f8fafc" stroke="#e2e8f0" dash={[2,2]} />
                  <Text text="HEADER TITLE / CONTACT INFO AREA" x={mmToPx(settings.logoWidth || 35) + mmToPx(15)} y={mmToPx(15)} fontSize={mmToPx(4)} fontStyle="bold" fill="#cbd5e1" opacity={0.5} />
                </Group>

                {/* Custom Elements */}
                {settings.customElements?.map(el => (
                  <Group 
                    key={el.id} 
                    x={mmToPx(el.x)} 
                    y={mmToPx(el.y)} 
                    draggable 
                    onDragEnd={e => handleDragEnd(e, `custom-${el.id}`)}
                    onClick={() => setSelectedId(el.id)}
                    onTap={() => setSelectedId(el.id)}
                  >
                    {el.type === 'text' && (
                      <Text 
                        id={`custom-${el.id}`}
                        text={el.text} 
                        fontSize={mmToPx(el.fontSize || 10)} 
                        fill={el.fill} 
                        stroke={selectedId === el.id ? '#ef4444' : undefined}
                        strokeWidth={selectedId === el.id ? 0.5 : 0}
                        padding={5}
                        onTransformEnd={handleTransformEnd}
                      />
                    )}
                    {el.type === 'rect' && (
                      <Rect 
                        id={`custom-${el.id}`}
                        width={mmToPx(el.width || 50)} 
                        height={mmToPx(el.height || 20)} 
                        fill={el.fill}
                        stroke={selectedId === el.id ? '#ef4444' : el.stroke || '#000'}
                        strokeWidth={selectedId === el.id ? 1.5 : el.strokeWidth || 0.5}
                        onTransformEnd={handleTransformEnd}
                      />
                    )}
                    {el.type === 'line' && (
                      <Line 
                        id={`custom-${el.id}`}
                        points={el.points?.map(p => mmToPx(p)) || [0, 0, mmToPx(50), 0]} 
                        stroke={selectedId === el.id ? '#ef4444' : el.stroke || '#000'}
                        strokeWidth={selectedId === el.id ? 2 : el.strokeWidth || 1}
                        onTransformEnd={handleTransformEnd}
                      />
                    )}
                    {el.type === 'image' && el.id.includes('sign') && signatureUrl ? (
                      <Group>
                         <URLImage 
                           url={signatureUrl}
                           x={0}
                           y={0}
                           width={mmToPx(el.width || 50)} 
                           height={mmToPx(el.height || 20)} 
                           id={`custom-${el.id}`}
                           onTransformEnd={handleTransformEnd}
                         />
                      </Group>
                    ) : el.type === 'image' ? (
                      <>
                        <Rect 
                          id={`custom-${el.id}`}
                          width={mmToPx(el.width || 50)} 
                          height={mmToPx(el.height || 20)} 
                          fill="#f1f5f9"
                          stroke={selectedId === el.id ? '#ef4444' : '#64748b'}
                          strokeWidth={selectedId === el.id ? 1 : 0.5}
                          dash={[4,4]}
                          onTransformEnd={handleTransformEnd}
                        />
                        <Text 
                          text="IMAGE" 
                          width={mmToPx(el.width || 50)} 
                          y={mmToPx((el.height || 20) / 2 - 2)} 
                          align="center" 
                          fontSize={mmToPx(3)} 
                          fontStyle="bold" 
                          fill="#94a3b8" 
                        />
                      </>
                    ) : null}
                  </Group>
                ))}

                {/* Grid Sections */}
                {settings.sections?.map(sec => (
                  <Group
                    key={sec.id}
                    id={sec.id}
                    x={mmToPx(sec.x)}
                    y={mmToPx(sec.y)}
                    draggable
                    onDragEnd={e => handleDragEnd(e, sec.id)}
                    onClick={() => setSelectedId(sec.id)}
                  >
                    <Rect 
                      width={mmToPx(sec.width)} 
                      height={mmToPx(sec.height)} 
                      fill="white" 
                      stroke={selectedId === sec.id ? '#f97316' : '#e2e8f0'} 
                      strokeWidth={selectedId === sec.id ? 2 : 1}
                      onTransformEnd={handleTransformEnd}
                    />
                    
                    {/* Grid Lines */}
                    {Array.from({ length: sec.rows + 1 }).map((_, r) => (
                      <Line 
                        key={`r${r}`} 
                        points={[0, (mmToPx(sec.height) / sec.rows) * r, mmToPx(sec.width), (mmToPx(sec.height) / sec.rows) * r]} 
                        stroke="#f1f5f9" strokeWidth={0.5} 
                      />
                    ))}
                    {Array.from({ length: sec.cols + 1 }).map((_, c) => (
                      <Line 
                        key={`c${c}`} 
                        points={[(mmToPx(sec.width) / sec.cols) * c, 0, (mmToPx(sec.width) / sec.cols) * c, mmToPx(sec.height)]} 
                        stroke="#f1f5f9" strokeWidth={0.5} 
                      />
                    ))}

                    {/* Cell Content Placeholders */}
                    {Array.from({ length: sec.rows }).map((_, r) => 
                      Array.from({ length: sec.cols }).map((_, c) => {
                        const field = sec.fields?.[`${r}-${c}`];
                        if (!field) return null;
                        return (
                          <Text
                            key={`${r}-${c}`}
                            text={`[${field.toUpperCase()}]`}
                            x={(mmToPx(sec.width) / sec.cols) * c}
                            y={(mmToPx(sec.height) / sec.rows) * r + (mmToPx(sec.height) / sec.rows / 2) - 2}
                            width={mmToPx(sec.width) / sec.cols}
                            align="center"
                            fontSize={mmToPx(3)}
                            fill="#f97316"
                            fontStyle="bold"
                          />
                        );
                      })
                    )}

                    <Text 
                      text={sec.name} 
                      x={mmToPx(2)} 
                      y={-mmToPx(4)} 
                      fontSize={mmToPx(3)} 
                      fontStyle="bold" 
                      fill="#f97316" 
                      opacity={selectedId === sec.id ? 1 : 0.4}
                    />
                  </Group>
                ))}

                {/* Transformer for custom elements */}
                <Transformer
                  ref={transformerRef}
                  boundBoxFunc={(oldBox, newBox) => {
                    // Limit minimum size
                    if (newBox.width < 10 || newBox.height < 10) {
                      return oldBox;
                    }
                    return newBox;
                  }}
                />

                {/* Draggable Info Blocks */}
                <Group x={tableX} y={mmToPx(settings.infoBoxesY)} draggable onDragEnd={e => handleDragEnd(e, 'infoBoxesY')} dragBoundFunc={p => ({ x: tableX, y: p.y })}>
                  <Rect width={tableW} height={mmToPx(35)} fill="#fff" stroke="#fecaca" strokeWidth={1} cornerRadius={2} />
                  <Line points={[tableW/2, 0, tableW/2, mmToPx(35)]} stroke="#fecaca" strokeWidth={0.5} />
                  <Text text="CLIENT / DOCUMENT INFO" x={mmToPx(5)} y={mmToPx(5)} fontSize={mmToPx(3)} fontStyle="bold" fill="#f87171" />
                </Group>

                {/* Draggable Description Box */}
                <Group x={tableX} y={mmToPx(settings.descriptionStartY)} draggable onDragEnd={e => handleDragEnd(e, 'descriptionStartY')} dragBoundFunc={p => ({ x: tableX, y: p.y })}>
                   <Rect width={tableW} height={mmToPx(12)} fill="#fef2f2" stroke="#b91c1c" strokeWidth={1} />
                   <Text text="DESCRIPTION STATEMENT (RE:)" x={0} y={mmToPx(4)} width={tableW} align="center" fontSize={mmToPx(3.5)} fontStyle="bold" fill="#b91c1c" />
                </Group>

                {/* Signature Bounding Box */}
                <Group x={tableX} y={mmToPx(settings.footerY + 10)}>
                  <Rect 
                    width={tableW / 2} 
                    height={mmToPx(settings.signatureHeight)} 
                    fill="#f0f9ff" 
                    stroke="#0ea5e9" 
                    strokeWidth={1} 
                    dash={[4, 2]} 
                    cornerRadius={2}
                    draggable
                    onDragEnd={e => {
                      const newY = (e.target.y() / SCALE) - (settings.footerY + 10);
                      // This is just a visual guide in designer, signature height doesn't usually move independently of footer in layout
                    }}
                  />
                  <Line 
                    points={[mmToPx(5), mmToPx(settings.signatureHeight - 8), mmToPx(55), mmToPx(settings.signatureHeight - 8)]} 
                    stroke="#0ea5e9" 
                    strokeWidth={0.5} 
                    dash={[1, 1]} 
                  />
                  <Text 
                    text="SIGNATURE AREA GUIDE" 
                    x={mmToPx(5)} 
                    y={mmToPx(settings.signatureHeight - 12)} 
                    fontSize={mmToPx(2.5)} 
                    fontStyle="bold" 
                    fill="#0ea5e9" 
                  />
                  <Text 
                    text=".................................................." 
                    x={mmToPx(5)} 
                    y={mmToPx(settings.signatureHeight - 6)} 
                    fontSize={mmToPx(3)} 
                    fill="#0ea5e9" 
                    opacity={0.5}
                  />
                  <Text 
                    text="Signatory Name" 
                    x={mmToPx(5)} 
                    y={mmToPx(settings.signatureHeight - 3)} 
                    fontSize={mmToPx(2.5)} 
                    fill="#0ea5e9" 
                  />
                </Group>

                {/* Items Table with Adjustable Columns */}
                <Group 
                  x={tableX} 
                  y={mmToPx(settings.itemsStartY || (settings.descriptionStartY + 8))}
                  draggable
                  dragBoundFunc={p => ({ x: tableX, y: p.y })}
                  onDragEnd={e => handleDragEnd(e, 'itemsStartY')}
                >
                  <Rect width={tableW} height={mmToPx(settings.itemsRowHeight * 10 + 10)} fill="#fff" stroke="#e2e8f0" strokeWidth={1} />
                  <Rect width={tableW} height={mmToPx(8)} fill="#f8fafc" stroke="#e2e8f0" />
                  
                  {/* Column Resizer Handles (Visual indicators) */}
                  {(() => {
                    const itemsY = settings.itemsStartY || (settings.descriptionStartY + 8);
                    const cw = settings.columnWidths || DEFAULT_SETTINGS.columnWidths!;
                    const snX = mmToPx(cw.sn);
                    const qtyX = tableW - mmToPx(cw.qty + cw.unit + cw.rate + cw.amount);
                    const unitX = tableW - mmToPx(cw.unit + cw.rate + cw.amount);
                    const rateX = tableW - mmToPx(cw.rate + cw.amount);
                    const amountX = tableW - mmToPx(cw.amount);

                    return (
                      <>
                        {/* Lines */}
                        <Line points={[snX, 0, snX, mmToPx(settings.itemsRowHeight * 10 + 10)]} stroke="#e2e8f0" strokeWidth={1} />
                        <Line points={[qtyX, 0, qtyX, mmToPx(settings.itemsRowHeight * 10 + 10)]} stroke="#e2e8f0" strokeWidth={1} />
                        <Line points={[unitX, 0, unitX, mmToPx(settings.itemsRowHeight * 10 + 10)]} stroke="#e2e8f0" strokeWidth={1} />
                        <Line points={[rateX, 0, rateX, mmToPx(settings.itemsRowHeight * 10 + 10)]} stroke="#e2e8f0" strokeWidth={1} />
                        <Line points={[amountX, 0, amountX, mmToPx(settings.itemsRowHeight * 10 + 10)]} stroke="#e2e8f0" strokeWidth={1} />

                        {/* Interactive Drag Handles */}
                        <Rect x={snX - 3} y={0} width={6} height={mmToPx(8)} fill="transparent" draggable dragBoundFunc={p => ({ x: p.x, y: mmToPx(itemsY) })} onDragMove={e => handleColumnResize('sn', e.target.x() + tableX)} />
                        <Rect x={qtyX - 3} y={0} width={6} height={mmToPx(8)} fill="transparent" draggable dragBoundFunc={p => ({ x: p.x, y: mmToPx(itemsY) })} onDragMove={e => handleColumnResize('qty', e.target.x() + tableX)} />
                        <Rect x={unitX - 3} y={0} width={6} height={mmToPx(8)} fill="transparent" draggable dragBoundFunc={p => ({ x: p.x, y: mmToPx(itemsY) })} onDragMove={e => handleColumnResize('unit', e.target.x() + tableX)} />
                        <Rect x={rateX - 3} y={0} width={6} height={mmToPx(8)} fill="transparent" draggable dragBoundFunc={p => ({ x: p.x, y: mmToPx(itemsY) })} onDragMove={e => handleColumnResize('rate', e.target.x() + tableX)} />
                        
                        {/* Hover markers */}
                        {[snX, qtyX, unitX, rateX].map((x, i) => (
                           <Rect key={i} x={x-1} y={0} width={2} height={mmToPx(8)} fill="#ef4444" opacity={0} onMouseEnter={(e: any) => { e.target.opacity(0.5); const s = e.target.getStage(); if (s) s.batchDraw(); }} onMouseLeave={(e: any) => { e.target.opacity(0); const s = e.target.getStage(); if (s) s.batchDraw(); }} />
                        ))}

                        <Text text="S/N" x={mmToPx(2)} y={mmToPx(2.5)} fontSize={mmToPx(3)} fontStyle="bold" fill="#64748b" />
                        <Text text="DECRIPTION" x={mmToPx(cw.sn + 2)} y={mmToPx(2.5)} fontSize={mmToPx(3)} fontStyle="bold" fill="#64748b" />
                        <Text text="QTY" x={qtyX + 2} y={mmToPx(2.5)} fontSize={mmToPx(2.5)} fontStyle="bold" fill="#64748b" />
                        <Text text="RATE" x={rateX + 2} y={mmToPx(2.5)} fontSize={mmToPx(2.5)} fontStyle="bold" fill="#64748b" />
                        
                        {/* Height Squeeze Handle */}
                        <Rect 
                          x={0} 
                          y={mmToPx(settings.itemsRowHeight * 10 + 10) - 5} 
                          width={tableW} 
                          height={10} 
                          fill="transparent" 
                          draggable 
                          dragBoundFunc={p => ({ x: tableX, y: p.y })}
                          onDragMove={e => {
                            const newYRelative = (e.target.y() / SCALE);
                            const newRowHeight = (newYRelative - 10) / 10;
                            setSettings(prev => ({ ...prev, itemsRowHeight: Math.max(2, Number(newRowHeight.toFixed(2))) }));
                          }}
                          onMouseEnter={e => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'row-resize'; }}
                          onMouseLeave={e => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'default'; }}
                        />
                      </>
                    );
                  })()}
                </Group>

                {/* Footer Boxes */}
                <Group x={tableX} y={mmToPx(settings.footerY)} draggable onDragEnd={e => handleDragEnd(e, 'footerY')} dragBoundFunc={p => ({ x: tableX, y: p.y })}>
                   <Rect width={tableW} height={mmToPx(settings.footerHeight)} fill="#f8fafc" stroke="#475569" strokeWidth={1} dash={[1,2]} />
                   
                   {/* Footer Resize Handle */}
                   <Rect 
                    x={0} 
                    y={mmToPx(settings.footerHeight) - 5} 
                    width={tableW} 
                    height={10} 
                    fill="transparent"
                    draggable 
                    dragBoundFunc={p => ({ x: tableX, y: p.y })}
                    onDragMove={e => {
                      const newHeight = (e.target.y() / SCALE) + (5 / SCALE);
                      setSettings(prev => ({ ...prev, footerHeight: Math.max(20, Math.round(newHeight)) }));
                    }}
                    onMouseEnter={e => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'row-resize'; }}
                    onMouseLeave={e => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'default'; }}
                   />
                   <Line 
                    points={[tableW/2 - 10, mmToPx(settings.footerHeight) - 2, tableW/2 + 10, mmToPx(settings.footerHeight) - 2]} 
                    stroke="#475569" 
                    strokeWidth={1} 
                   />

                   <Text text="FOOTER REGION (TOTALS & SIGNATORY)" x={0} y={mmToPx(settings.footerHeight/2 - 2)} width={tableW} align="center" fontSize={mmToPx(3.5)} fontStyle="bold" fill="#475569" />
                </Group>

                <Text text="PAGE BOUNDARY" x={mmToPx(PAGE_WIDTH_MM / 2 - 15)} y={mmToPx(PAGE_HEIGHT_MM - 4)} fontSize={mmToPx(2.5)} fontStyle="bold" fill="#cbd5e1" />
              </Layer>
            </Stage>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDesigner;
